import constants from "constants";
import { getStorage, setStorage } from "utils";

export const OFFICE_NAVIGATION_STORAGE_KEY = "isFromOwnerOfficeModule";
export const OFFICE_NAV_CONTEXT_CHANGE_EVENT = "officeNavContextChange";

/**
 * @typedef {Object} OfficeNavigationContext
 * @property {boolean} officeSlection
 * @property {boolean} staffOfficeSlection
 * @property {boolean} isAdminControl
 * @property {number|null} officeId
 * @property {string} [officeName]
 * @property {number|null} [ownerId]
 */

const DEFAULT_OFFICE_NAVIGATION_CONTEXT = {
  officeSlection: false,
  staffOfficeSlection: false,
  isAdminControl: false,
  officeId: null,
  officeName: null,
  ownerId: null,
};

/** Notifies listeners that office navigation context or related caches changed. */
function dispatchOfficeNavContextChange() {
  window.dispatchEvent(new CustomEvent(OFFICE_NAV_CONTEXT_CHANGE_EVENT));
}

/**
 * Returns true when the user navigated from a specific office (not global menu).
 * @param {OfficeNavigationContext|null|undefined} context
 */
export function isInOfficeNavigationContext(context) {
  if (!context?.officeId) return false;
  return !!(
    context.officeSlection ||
    context.staffOfficeSlection ||
    context.isAdminControl
  );
}

/** Reads the persisted office navigation context from storage. */
export function getOfficeNavigationContext() {
  return (
    getStorage(OFFICE_NAVIGATION_STORAGE_KEY) || DEFAULT_OFFICE_NAVIGATION_CONTEXT
  );
}

/**
 * Persists office navigation context when entering an office dashboard.
 * @param {Partial<OfficeNavigationContext>} context
 */
export function setOfficeNavigationContext(context) {
  setStorage(OFFICE_NAVIGATION_STORAGE_KEY, {
    ...DEFAULT_OFFICE_NAVIGATION_CONTEXT,
    ...context,
  });
  dispatchOfficeNavContextChange();
}

/** Clears office-specific filter caches used by global modules. */
export function clearOfficeModuleFilterCaches() {
  const tasksCache = constants.tasks?.cache;
  const notesCache = constants.notes?.cache;
  const officePoliciesCache = constants.officePolicies?.cache;
  const agendaKeys = constants.localStorageKeys?.agenda;

  if (tasksCache?.myTaskFilter) {
    setStorage(tasksCache.myTaskFilter, {
      offices: [],
      staff: [],
      status: [],
      priority: [],
    });
  }
  if (notesCache?.myNotesFilter) {
    setStorage(notesCache.myNotesFilter, {
      offices: [],
      staff: [],
      status: null,
    });
  }
  if (notesCache?.transferedNoteFilter) {
    setStorage(notesCache.transferedNoteFilter, {
      offices: [],
      staff: [],
      status: null,
    });
  }
  if (notesCache?.dashboardFilter) {
    setStorage(notesCache.dashboardFilter, {
      offices: [],
      staff: [],
      agenda: [],
      viewSettig: [],
      isScheduler: false,
    });
  }
  if (officePoliciesCache?.myOfficePolicyFilter) {
    setStorage(officePoliciesCache.myOfficePolicyFilter, {
      offices: [],
      staff: [],
      status: [],
      categoryIds: [],
      fromDate: null,
      toDate: null,
    });
  }
  if (officePoliciesCache?.staffOfficePolicyFilter) {
    setStorage(officePoliciesCache.staffOfficePolicyFilter, {
      offices: [],
      staff: [],
      status: [],
      priority: [],
    });
  }
  if (agendaKeys?.officeFilter) {
    sessionStorage.setItem(agendaKeys.officeFilter, JSON.stringify([]));
  }
  if (constants.localStorageKeys?.staffAvailablilityFilter) {
    sessionStorage.setItem(
      constants.localStorageKeys.staffAvailablilityFilter,
      JSON.stringify({ apiOffices: [], apiEmps: [] }),
    );
  }
  if (agendaKeys?.staffSchedulerEnterprise) {
    setStorage(agendaKeys.staffSchedulerEnterprise, false);
  }
}

/**
 * Clears office navigation context and related module filter caches.
 * Call when navigating through the global menu/header.
 */
export function clearOfficeNavigationContext() {
  setStorage(OFFICE_NAVIGATION_STORAGE_KEY, DEFAULT_OFFICE_NAVIGATION_CONTEXT);
  clearOfficeModuleFilterCaches();
  dispatchOfficeNavContextChange();
}

/**
 * Resolves the owner ID that API calls should use based on navigation context.
 * @param {number|null|undefined} globalOwnerId - Owner selected in the global switcher
 * @param {OfficeNavigationContext|null|undefined} [context]
 */
export function getEffectiveOwnerId(globalOwnerId, context = getOfficeNavigationContext()) {
  if (isInOfficeNavigationContext(context) && context?.ownerId) {
    return context.ownerId;
  }
  return globalOwnerId;
}

/**
 * Finds an account owner in the staff owner list by id (type-safe).
 * @param {Array<{ id?: number|string }>|null|undefined} ownerList
 * @param {number|string|null|undefined} ownerId
 */
export function resolveOwnerFromOwnerList(ownerList, ownerId) {
  if (ownerId == null || ownerId === "" || !Array.isArray(ownerList)) {
    return null;
  }
  return (
    ownerList.find((owner) => Number(owner?.id) === Number(ownerId)) ?? null
  );
}
